import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStandaloneToast, type UseToastOptions } from '@chakra-ui/react';
import type { RootState } from '../../store';
import { hideMessage } from '../../reducers/message-slice';

const { toast } = createStandaloneToast();

const toastDefaults: Partial<UseToastOptions> = {
  duration: 3000,
  isClosable: true,
  position: 'bottom',
};

export const showToast = (options: UseToastOptions) => {
  toast({
    ...toastDefaults,
    ...options,
  });
};

export const showErrorToast = (
  message: string,
  description?: string,
  timeout?: number,
) => {
  showToast({
    title: message,
    description,
    status: 'error',
    duration: timeout || 3000,
  });
};

export const showSuccessToast = (
  message: string,
  description?: string,
  timeout?: number,
) => {
  showToast({
    title: message,
    description,
    status: 'success',
    duration: timeout || 3000,
  });
};

export const SnackBar: React.FC = () => {
  const { value, type, timeout } = useSelector(
    (state: RootState) => state.message || {},
  );
  const dispatch = useDispatch();
  const lastMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!value || !type) {
      return;
    }

    const messageKey = `${type}-${value}`;
    if (lastMessageRef.current === messageKey) {
      return;
    }

    lastMessageRef.current = messageKey;

    switch (type) {
      case 'error':
        showErrorToast(value, undefined, timeout);
        break;
      case 'success':
        showSuccessToast(value, undefined, timeout);
        break;
      default:
        showToast({
          title: value,
          status: (type as UseToastOptions['status']) || 'info',
          duration: timeout || 3000,
        });
        break;
    }

    dispatch(hideMessage());

    const reset = setTimeout(() => {
      lastMessageRef.current = null;
    }, 100);

    return () => clearTimeout(reset);
  }, [value, type, timeout, dispatch]);

  return null;
};

export default SnackBar;
